import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

import { RequestError } from './RequestError';
import { StepChangedNotice } from './StepChangedNotice';
import { REASON_MAX_LENGTH, useReverseCutoverMutation } from './useCutover';

import type { ReverseTarget } from './cutover.types';

type TargetKind = ReverseTarget['kind'];

const STEP_LABELS = ['0%', '10%', '50%', '100%'];

export function MoveBackDialog({
  step,
  hasStepChanged,
  onClose,
}: {
  step: number;
  hasStepChanged: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const reverse = useReverseCutoverMutation();
  const [kind, setKind] = useState<TargetKind>(step > 0 ? 'step' : 'user');
  const [userIdText, setUserIdText] = useState('');
  const [reason, setReason] = useState('');

  const userId = Number(userIdText);
  const hasValidUserId = Number.isInteger(userId) && userId > 0;
  const target: ReverseTarget | null =
    kind === 'step'
      ? { kind, step }
      : kind === 'all'
        ? { kind }
        : hasValidUserId
          ? { kind, user_id: userId }
          : null;
  const trimmedReason = reason.trim();
  const canSubmit =
    !reverse.isPending &&
    !hasStepChanged &&
    target !== null &&
    trimmedReason !== '';
  const confirmLabel =
    kind === 'step'
      ? `Move ${STEP_LABELS[step]} back to ${STEP_LABELS[step - 1]}`
      : kind === 'all'
        ? 'Move everyone back to Python'
        : `Move user ${hasValidUserId ? userId : ''} back to Python`;

  const submit = () => {
    if (target === null) return;
    reverse.mutate(
      { target, reason: trimmedReason },
      {
        onSuccess: (result) => {
          if (!result.accepted) return;
          toast.success('The move back is queued');
          onClose();
        },
      }
    );
  };

  const option = (value: TargetKind, label: string, disabled = false) => (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="radio"
        name="move-back-target"
        checked={kind === value}
        disabled={disabled}
        onChange={() => setKind(value)}
      />
      {label}
    </label>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move users back to Python</DialogTitle>
          <DialogDescription>
            The users you choose move back to the Python backend. Their routines
            move with them.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-2">
          <legend className="mb-2 text-sm font-medium">Who moves back</legend>
          {option(
            'step',
            step > 0
              ? `One step: the users ${STEP_LABELS[step]} added. The cutover goes back to ${STEP_LABELS[step - 1]}.`
              : 'One step (the cutover has no step to move back)',
            step === 0
          )}
          {option('user', 'One user. Later steps leave this user on Python.')}
          {kind === 'user' && (
            <Input
              aria-label="User ID"
              inputMode="numeric"
              placeholder="User ID"
              value={userIdText}
              onChange={(event) => setUserIdText(event.target.value)}
            />
          )}
          {option('all', 'Everyone the cutover moved.')}
        </fieldset>
        <label className="block space-y-2 text-sm font-medium">
          <span>Reason</span>
          <Textarea
            value={reason}
            maxLength={REASON_MAX_LENGTH}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why these users move back"
          />
        </label>
        {hasStepChanged && <StepChangedNotice />}
        <RequestError result={reverse.data} error={reverse.error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            disabled={!canSubmit}
            onClick={submit}
          >
            {reverse.isPending ? 'Starting...' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
